import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { Users, UserCog, Shield, Plus, ChevronLeft, ChevronRight, ArrowRight, Crown } from 'lucide-react';
import UnifiedTeamManagementModal from './UnifiedTeamManagementModal';
import { MobileTeamManagement } from './MobileTeamManagement';
import { PositionEditor } from './PositionEditor';
import { teamService } from '@/services/teamService';
import { photoUploadService } from '@/services/photoUploadService';
import { toast } from 'sonner';
import { useIsMobile } from '@/hooks/use-mobile';
import { useNavigate } from 'react-router-dom';

interface TeamMember {
  user_id: string;
  position?: string;
  role?: string;
  joined_at?: string;
  added_at?: string;
  user_details?: {
    username?: string;
    first_name?: string;
    last_name?: string;
    profile_photo_url?: string;
    slug?: string;
    age?: number;
    age_group?: string;
  };
}

interface TeamMembersSectionProps {
  teamId: number;
  teamName: string;
  teamSport?: string;
  currentUserId?: string;
  canManage: boolean;
  setShowPlayerForm?: (show: boolean) => void;
  setShowCoachForm?: (show: boolean) => void;
  setShowSupportForm?: (show: boolean) => void;
}

export const TeamMembersSection = ({ teamId, teamName, teamSport, currentUserId, canManage, setShowPlayerForm, setShowCoachForm, setShowSupportForm }: TeamMembersSectionProps) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [players, setPlayers] = useState<TeamMember[]>([]);
  const [coaches, setCoaches] = useState<TeamMember[]>([]);
  const [supportStaff, setSupportStaff] = useState<TeamMember[]>([]);
  const [teamOwner, setTeamOwner] = useState<TeamMember | null>(null);
  const [showOwnerPublic, setShowOwnerPublic] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showManagementModal, setShowManagementModal] = useState(false);
  
  // Separate current index state for each section
  const [playersCurrentIndex, setPlayersCurrentIndex] = useState(0);
  const [coachesCurrentIndex, setCoachesCurrentIndex] = useState(0);
  const [supportCurrentIndex, setSupportCurrentIndex] = useState(0);

  useEffect(() => {
    loadTeamMembers();
  }, [teamId]);

  const loadTeamMembers = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadPlayers(),
        loadCoaches(),
        loadSupportStaff(),
        loadTeamOwner()
      ]);
    } catch (error) {
      console.error('Error loading team members:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTeamOwner = async () => {
    try {
      // Get the team data including creator and visibility preference
      const { data: teamData, error: teamError } = await supabase
        .from('teams')
        .select('created_by, show_owner_public')
        .eq('team_id', teamId)
        .single();

      if (teamError || !teamData?.created_by) return;

      setShowOwnerPublic(teamData.show_owner_public ?? false);

      // Check if creator is a team-owner role
      const { data: internalUser } = await supabase
        .from('internal_users')
        .select('role')
        .eq('id', teamData.created_by)
        .maybeSingle();

      if (internalUser?.role === 'team-owner') {
        const ownerDetails = await getUserDetails(teamData.created_by);
        setTeamOwner({
          user_id: teamData.created_by,
          role: 'Team Owner',
          user_details: ownerDetails
        });
      }
    } catch (error) {
      console.error('Error loading team owner:', error);
    }
  };

  const handleToggleOwnerVisibility = async (checked: boolean) => {
    try {
      const { error } = await supabase
        .from('teams')
        .update({ show_owner_public: checked })
        .eq('team_id', teamId);

      if (error) throw error;

      setShowOwnerPublic(checked);
    } catch (error) {
      console.error('Error updating owner visibility:', error);
    }
  };

  const loadPlayers = async () => {
    try {
      const { data: teamPlayers, error } = await supabase
        .from('team_players')
        .select('*')
        .eq('team_id', teamId);

      if (error) throw error;

      const playersWithDetails = await Promise.all(
        (teamPlayers || []).map(async (player) => {
          const userDetails = await getUserDetails(player.user_id);
          return {
            ...player,
            user_details: userDetails
          };
        })
      );

      setPlayers(playersWithDetails);
    } catch (error) {
      console.error('Error loading players:', error);
    }
  };

  const loadCoaches = async () => {
    try {
      const { data: teamCoaches, error } = await supabase
        .from('team_coaches')
        .select('*')
        .eq('team_id', teamId);

      if (error) throw error;

      const coachesWithDetails = await Promise.all(
        (teamCoaches || []).map(async (coach) => {
          const userDetails = await getUserDetails(coach.coach_id);
          return {
            user_id: coach.coach_id,
            role: coach.role,
            added_at: coach.added_at,
            user_details: userDetails
          };
        })
      );

      // Get the team creator info to check their role
      const { data: teamData, error: teamError } = await supabase
        .from('teams')
        .select('created_by')
        .eq('team_id', teamId)
        .single();

      if (!teamError && teamData?.created_by) {
        // Check if team creator is already listed as a coach
        const isAlreadyCoach = coachesWithDetails.some(coach => coach.user_id === teamData.created_by);
        
        if (!isAlreadyCoach) {
          // Check if the creator is a team-owner - if so, don't add them as a coach
          const { data: internalUser } = await supabase
            .from('internal_users')
            .select('role')
            .eq('id', teamData.created_by)
            .maybeSingle();

          // Only add as Primary Coach if they're actually a coach role, not a team-owner
          if (internalUser?.role !== 'team-owner') {
            const creatorDetails = await getUserDetails(teamData.created_by);
            if (creatorDetails) {
              coachesWithDetails.unshift({
                user_id: teamData.created_by,
                role: 'Primary Coach',
                added_at: teamData.created_by,
                user_details: creatorDetails
              });
            }
          }
        }
      }

      setCoaches(coachesWithDetails);
    } catch (error) {
      console.error('Error loading coaches:', error);
    }
  };

  const loadSupportStaff = async () => {
    try {
      const { data: supportConnections, error } = await supabase
        .from('team_support_staff')
        .select('*')
        .eq('team_id', teamId)
        .eq('is_active', true);

      if (error) throw error;

      const supportWithDetails = await Promise.all(
        (supportConnections || []).map(async (support) => {
          const userDetails = await getUserDetails(support.staff_id);
          return {
            user_id: support.staff_id,
            role: support.role || 'Support Staff',
            position: support.position,
            added_at: support.added_at,
            user_details: userDetails
          };
        })
      );

      setSupportStaff(supportWithDetails);
    } catch (error) {
      console.error('Error loading support staff:', error);
    }
  };

  const getUserDetails = async (userId: string) => {
    try {
      // Try internal users first for better under-13 user support
      const { data: internalUser } = await supabase
        .from('internal_users')
        .select('username, role, age_group')
        .eq('id', userId)
        .maybeSingle();

      if (internalUser) {
        // Get profile data including age information
        const { data: profile } = await supabase
          .from('internal_user_profiles')
          .select('age, profile_photo_url')
          .eq('user_id', userId)
          .maybeSingle();

        // Convert relative profile photo path to signed URL for internal users
        let profilePhotoUrl = null;
        if (profile?.profile_photo_url && !profile.profile_photo_url.startsWith('http')) {
          try {
            profilePhotoUrl = await photoUploadService.getProfilePhotoUrl(profile.profile_photo_url);
          } catch (error) {
            console.error('Error getting signed URL for profile photo:', error);
            profilePhotoUrl = null;
          }
        } else {
          profilePhotoUrl = profile?.profile_photo_url || null;
        }

        return {
          username: internalUser.username,
          first_name: '',
          last_name: '',
          profile_photo_url: profilePhotoUrl,
          age: profile?.age || null,
          age_group: internalUser.age_group
        };
      }

      // Fallback to Supabase profiles
      const { data: profile } = await supabase
        .from('profiles')
        .select('username, first_name, last_name, profile_photo_url, slug')
        .eq('id', userId)
        .single();

      if (profile) return profile;

      return {};
    } catch (error) {
      console.error('Error fetching user details:', error);
      return {};
    }
  };

  const handlePositionUpdate = async (userId: string, newValue: string, memberType: 'player' | 'coach' | 'support') => {
    try {
      switch (memberType) {
        case 'player':
          await teamService.updatePlayerPosition(teamId, userId, newValue);
          break;
        case 'coach':
          await teamService.updateCoachRole(teamId, userId, newValue);
          break;
        case 'support':
          await teamService.updateSupportStaffRole(teamId, userId, newValue);
          break;
      }
      
      // Reload the specific section
      switch (memberType) {
        case 'player':
          await loadPlayers();
          break;
        case 'coach':
          await loadCoaches();
          break;
        case 'support':
          await loadSupportStaff();
          break;
      }
      
      toast.success(`${memberType === 'player' ? 'Position' : 'Role'} updated successfully`);
    } catch (error) {
      console.error('Error updating position/role:', error);
      toast.error(`Failed to update ${memberType === 'player' ? 'position' : 'role'}`);
      throw error; // Re-throw so PositionEditor can handle the error
    }
  };

  const navigateToProfile = (member: TeamMember) => {
    // Use slug or username for profile navigation
    const profileIdentifier = member.user_details?.slug || 
                              member.user_details?.username;
    
    if (profileIdentifier) {
      navigate(`/${profileIdentifier}`);
    } else {
      // Fallback for users without slug/username
      console.warn('No profile identifier found for user:', member.user_details);
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="bg-gray-800 border-gray-700">
            <CardHeader>
              <div className="h-6 bg-gray-700 rounded animate-pulse"></div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="h-16 bg-gray-700 rounded animate-pulse"></div>
                <div className="h-16 bg-gray-700 rounded animate-pulse"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  // Use mobile version on mobile devices, desktop version otherwise
  if (isMobile) {
    return (
      <MobileTeamManagement
        teamId={teamId}
        teamName={teamName}
        teamSport={teamSport}
        currentUserId={currentUserId}
        canManage={canManage}
      />
    );
  }

  const renderSection = (title: string, members: TeamMember[], type: 'player' | 'coach' | 'support', icon: React.ReactNode) => {
    const membersPerPage = 3;
    const totalPages = Math.ceil(members.length / membersPerPage);
    
    // Get the appropriate current index and setter based on section type
    let currentIndex = 0;
    let setCurrentIndex: (value: number | ((prev: number) => number)) => void = () => {};
    
    switch (type) {
      case 'player':
        currentIndex = playersCurrentIndex;
        setCurrentIndex = setPlayersCurrentIndex;
        break;
      case 'coach':
        currentIndex = coachesCurrentIndex;
        setCurrentIndex = setCoachesCurrentIndex;
        break;
      case 'support':
        currentIndex = supportCurrentIndex;
        setCurrentIndex = setSupportCurrentIndex;
        break;
    }
    
    const handleSwipeLeft = () => {
      setCurrentIndex((prev) => prev < totalPages - 1 ? prev + 1 : 0);
    };

    const handleSwipeRight = () => {
      setCurrentIndex((prev) => prev > 0 ? prev - 1 : totalPages - 1);
    };

    const currentPageMembers = members.slice(currentIndex * membersPerPage, (currentIndex + 1) * membersPerPage);

    return (
      <Card className="bg-gray-800 border-gray-600 mb-6">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {icon}
              <span className="text-white font-semibold">
                {title} {teamSport && `(${teamSport})`}
              </span>
              {canManage && type === 'player' && setShowPlayerForm && (
                <Button
                  size="sm"
                  className="bg-sports-green text-black hover:bg-sports-green/80 font-bold px-3 py-1 text-xs rounded-md"
                  onClick={() => setShowPlayerForm(true)}
                >
                  MANAGE
                </Button>
              )}
              {canManage && type === 'coach' && setShowCoachForm && (
                <Button
                  size="sm"
                  className="bg-sports-green text-black hover:bg-sports-green/80 font-bold px-3 py-1 text-xs rounded-md"
                  onClick={() => setShowCoachForm(true)}
                >
                  MANAGE
                </Button>
              )}
              {canManage && type === 'support' && setShowSupportForm && (
                <Button
                  size="sm"
                  className="bg-sports-green text-black hover:bg-sports-green/80 font-bold px-3 py-1 text-xs rounded-md"
                  onClick={() => setShowSupportForm(true)}
                >
                  MANAGE
                </Button>
              )}
            </div>
            {/* Pagination dots */}
            {totalPages > 1 && (
              <div className="flex gap-2">
                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentIndex(i)}
                    className={`w-3 h-3 rounded-full transition-colors ${
                      i === currentIndex ? 'bg-sports-green' : 'bg-muted-foreground/30'
                    }`}
                  />
                ))}
              </div>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {members.length > 0 ? (
            <div className="relative px-4">
              <div className="flex gap-8 justify-center">
                {currentPageMembers.map((member) => (
                  <div key={member.user_id} className="text-center flex-1 min-w-0 max-w-[30%] relative bg-muted/10 rounded-lg p-4 border border-muted/20">
                    {/* Large circular profile photo */}
                    <div className="relative mx-auto mb-3">
                      <div className="w-24 h-24 mx-auto bg-muted rounded-full flex items-center justify-center overflow-hidden border-2 border-muted-foreground/20">
                        {member.user_details?.profile_photo_url ? (
                          <img
                            src={member.user_details.profile_photo_url}
                            alt={member.user_details?.username || 'User'}
                            className="w-full h-full rounded-full object-cover"
                          />
                        ) : (
                          <Users className="h-12 w-12 text-muted-foreground" />
                        )}
                      </div>
                      {/* Green arrow button positioned on top right edge */}
                      <button 
                        onClick={() => navigateToProfile(member)}
                        className="absolute -top-1 -right-1 w-6 h-6 bg-sports-green hover:bg-sports-green/80 rounded-sm flex items-center justify-center transition-colors"
                      >
                        <ArrowRight className="h-3 w-3 text-black" />
                      </button>
                    </div>
                    
                    {/* User name */}
                    <div className="text-white text-sm font-bold uppercase tracking-wide mb-1">
                      {member.user_details?.username || 
                       `${member.user_details?.first_name || ''} ${member.user_details?.last_name || ''}`.trim() ||
                       'Team Member'}
                    </div>
                    
                    {/* Age display */}
                    {member.user_details?.age && (
                      <div className="text-gray-400 text-xs mb-1">
                        Age: {member.user_details.age}
                      </div>
                    )}
                    
                    {/* Age group fallback */}
                    {!member.user_details?.age && member.user_details?.age_group && (
                      <div className="text-gray-400 text-xs mb-1">
                        {member.user_details.age_group}
                      </div>
                    )}
                    
                    {/* Position/Role Editor */}
                    <div className="text-gray-400 text-xs flex justify-center">
                      {canManage ? (
                        <PositionEditor
                          currentValue={member.position || member.role || ''}
                          memberType={type}
                          sport={teamSport}
                          onUpdate={(newValue) => handlePositionUpdate(member.user_id, newValue, type)}
                        />
                      ) : (
                        <span>{member.position || member.role || (type === 'player' ? 'Position' : 'Role')}</span>
                      )}
                    </div>
                  </div>
                ))}
                {/* Fill remaining slots to maintain spacing */}
                {Array.from({ 
                  length: Math.max(0, membersPerPage - currentPageMembers.length) 
                }, (_, i) => (
                  <div key={`empty-${i}`} className="flex-1 min-w-0 max-w-[30%] invisible">
                    <div className="w-24 h-24 mx-auto mb-3"></div>
                    <div className="text-sm mb-1">Hidden</div>
                  </div>
                ))}
              </div>
              
              {totalPages > 1 && (
                <>
                  <button
                    onClick={handleSwipeRight}
                    className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-2 bg-sports-green hover:bg-sports-green/80 text-black p-2 rounded-full shadow-lg z-10"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button
                    onClick={handleSwipeLeft}
                    className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-2 bg-sports-green hover:bg-sports-green/80 text-black p-2 rounded-full shadow-lg z-10"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-gray-400">No {title.toLowerCase()} added yet.</div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <>
      <div className="space-y-4">
        {/* Team Owner Section - only visible if opted in OR if user is the owner */}
        {teamOwner && (showOwnerPublic || currentUserId === teamOwner.user_id) && (
          <Card className="bg-gray-800 border-gray-600 mb-6">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Crown className="h-5 w-5 text-purple-400" />
                  <span className="text-white font-semibold">Team Owner</span>
                  {!showOwnerPublic && currentUserId === teamOwner.user_id && (
                    <Badge variant="outline" className="ml-2 text-xs">
                      Hidden from Public
                    </Badge>
                  )}
                </div>
                {/* Privacy toggle for team owner only */}
                {canManage && currentUserId === teamOwner.user_id && (
                  <div className="flex items-center gap-2">
                    <Label htmlFor="owner-visibility" className="text-xs text-muted-foreground">
                      Show Publicly
                    </Label>
                    <Switch
                      id="owner-visibility"
                      checked={showOwnerPublic}
                      onCheckedChange={handleToggleOwnerVisibility}
                    />
                  </div>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="flex gap-8 justify-center">
                <div className="text-center flex-1 min-w-0 max-w-[30%] relative bg-muted/10 rounded-lg p-4 border border-muted/20">
                  {/* Large circular profile photo */}
                  <div className="relative mx-auto mb-3">
                    <div className="w-24 h-24 mx-auto bg-muted rounded-full flex items-center justify-center overflow-hidden border-2 border-purple-400/50">
                      {teamOwner.user_details?.profile_photo_url ? (
                        <img
                          src={teamOwner.user_details.profile_photo_url}
                          alt={teamOwner.user_details?.username || 'Team Owner'}
                          className="w-full h-full rounded-full object-cover"
                        />
                      ) : (
                        <Crown className="h-12 w-12 text-purple-400" />
                      )}
                    </div>
                    {/* Green arrow button positioned on top right edge */}
                    <button 
                      onClick={() => navigateToProfile(teamOwner)}
                      className="absolute -top-1 -right-1 w-6 h-6 bg-sports-green hover:bg-sports-green/80 rounded-sm flex items-center justify-center transition-colors"
                    >
                      <ArrowRight className="h-3 w-3 text-black" />
                    </button>
                  </div>
                  
                  {/* User name */}
                  <div className="text-white text-sm font-bold uppercase tracking-wide mb-1">
                    {teamOwner.user_details?.username || 'Team Owner'}
                  </div>
                  
                  {/* Role badge */}
                  <div className="text-purple-400 text-xs font-medium">
                    Team Owner
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Players Section */}
        {renderSection('Players', players, 'player', <Users className="h-5 w-5" />)}
        
        {/* Coaches Section */}
        {renderSection('Coaches', coaches, 'coach', <UserCog className="h-5 w-5" />)}
        
        {/* Support Staff Section */}
        {renderSection('Support Staff', supportStaff, 'support', <Shield className="h-5 w-5" />)}
      </div>


      {/* Unified Team Management Modal */}
      <UnifiedTeamManagementModal
        isOpen={showManagementModal}
        onClose={() => setShowManagementModal(false)}
        team={{
          team_id: teamId,
          team_name: teamName,
          sport_type: teamSport
        }}
        onUpdate={loadTeamMembers}
      />
    </>
  );
};